import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Container,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import ResendVerification from '../components/auth/ResendVerification';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { login, expiredAccount } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await login(username, password);
            navigate('/');
        } catch (err) {
            if (!expiredAccount) {
                setError('Invalid username or password');
            }
        }
    };

    const handleSocialLogin = (provider: string) => {
        window.location.href = `/oauth2/authorization/${provider}`;
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundImage: 'url(/images/narralyze-bg.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    zIndex: 1,
                },
            }}
        >
            <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {/* Logo and Title Section */}
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <img 
                            src="/images/logo.png" 
                            alt="Narralyze Logo" 
                            style={{ 
                                height: '60px', 
                                marginBottom: '16px',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                            }} 
                        />
                        <Typography 
                            component="h1" 
                            variant="h3" 
                            sx={{ 
                                color: 'white',
                                fontWeight: 700,
                                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                mb: 1
                            }}
                        >
                            Welcome to Narralyze
                        </Typography>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: 'rgba(255,255,255,0.9)',
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                fontWeight: 300
                            }}
                        >
                            Qualitative Research Made Simple
                        </Typography>
                    </Box>

                    {/* Login Form */}
                    <Paper 
                        elevation={24} 
                        sx={{ 
                            p: 4, 
                            width: '100%',
                            maxWidth: 450,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                    >
                        {expiredAccount && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                                <Typography color="error.dark" align="center" gutterBottom>
                                    {expiredAccount.message}
                                </Typography>
                                <Typography align="center">
                                    <a 
                                        href={expiredAccount.renewalUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ color: 'inherit', textDecoration: 'underline' }}
                                    >
                                        Click here to renew your license
                                    </a>
                                </Typography>
                            </Box>
                        )}
                        {error && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                                <Typography color="error.dark" align="center">
                                    {error}
                                </Typography>
                            </Box>
                        )}
                        {error === 'Email not verified. Please check your email for a verification link.' && (
                            <ResendVerification />
                        )}
                        
                        <Box component="form" onSubmit={handleSubmit}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label="Username"
                                name="username"
                                autoComplete="username"
                                autoFocus
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ 
                                    mt: 4, 
                                    mb: 3,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                    }
                                }}
                            >
                                Sign In
                            </Button>
                            
                            <Divider sx={{ my: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                    OR
                                </Typography>
                            </Divider>
                            
                            <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                                <Grid item>
                                    <IconButton
                                        onClick={() => handleSocialLogin('google')}
                                        sx={{ 
                                            color: '#DB4437',
                                            bgcolor: 'white',
                                            border: '2px solid #DB4437',
                                            '&:hover': {
                                                bgcolor: '#DB4437',
                                                color: 'white',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                        title="Sign in with Google"
                                    >
                                        <GoogleIcon fontSize="large" />
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        onClick={() => handleSocialLogin('facebook')}
                                        sx={{ 
                                            color: '#1877F2',
                                            bgcolor: 'white',
                                            border: '2px solid #1877F2',
                                            '&:hover': {
                                                bgcolor: '#1877F2',
                                                color: 'white',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                        title="Sign in with Facebook"
                                    >
                                        <FacebookIcon fontSize="large" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                            
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <Link 
                                        to="/forgot-password" 
                                        style={{ 
                                            textDecoration: 'none',
                                            color: 'primary.main',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Forgot your password?
                                    </Link>
                                </Typography>
                                <Typography variant="body2">
                                    Don't have an account?{' '}
                                    <Link 
                                        to="/register" 
                                        style={{ 
                                            textDecoration: 'none',
                                            color: 'primary.main',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Sign up
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default LoginPage; 